import React, { useState, useEffect, useRef, useMemo, useCallback, useId } from 'react';
import { SiteWideBanner } from './SiteWideBanner';
import { LanguagePicker, Language } from './LanguagePicker';
import { SearchBar } from '../SearchBar/SearchBar';
import { CustomIcon } from '../CustomIcon/CustomIcon';
import { CustomIconName } from '../CustomIcon/CustomIcon';
import { Logo } from '../Logo/Logo';
import { CtaButton } from '../CtaButton/CtaButton';
import { CTA_PREFIXES, CtaTrackingPrefixContext } from '../../hooks/useClickTracking';
import { DesktopNav } from './DesktopNav';
import { MobileDrawer } from './MobileDrawer';
import { hasCtaDestination } from '../../utils/ctaAction';
import type { CtaAction } from '../../utils/ctaAction';
import type { CtaBehavior } from '../../utils/ctaBehavior';
import { buildLanguageUrl, detectLocaleFromUrl } from '../../utils/languageUrl';
import { hasOpenVisibleDialog, wrapTabFocus } from '../../utils/useDialog';
import { publishStickyOffset } from '../../utils/stickyOffset';
import './MegaMenu.scss';
import './SiteWideBanner.scss';
import './PrimaryNavItem.scss';

/**
 * MegaMenu - Navigation component for Polyphemus CMS
 *
 * A React port of the existing MegaMenu Lego widget with:
 * - Site-wide banner support
 * - Primary navigation (L1) with dropdown panels
 * - Secondary navigation (L2) with links and featured content
 * - Search integration
 * - Language picker
 * - CTA buttons
 * - Responsive desktop/mobile views
 * - Sticky navigation on scroll
 *
 * State management lives here; DesktopNav and MobileDrawer are presentational.
 */

// Data structure interfaces
import type { ColumnVariant } from './PanelLayout';

export interface NavLink {
  /** Link display text */
  title: string;
  /** Link destination URL */
  url: string;
  /** Optional analytics tracking ID */
  id?: string;
  /** Optional icon image URL */
  iconUrl?: string;
  /** Optional icon alt text */
  iconAlt?: string;
  /** Optional badge text */
  badgeText?: string;
  /** Optional link description */
  description?: string;
  /** Column alignment for dual column layout */
  align?: 'left' | 'right';
}

export interface SecondaryNavGroup {
  /** Panel title */
  title: string;
  /** Visual variant controlling how links render in this column */
  variant: ColumnVariant;
  /** Column layout type */
  columnType: 'single' | 'dual';
  /** Optional action link text at bottom of panel */
  actionText?: string;
  /** Optional action link URL */
  actionUrl?: string;
  /** Optional action link CTA ID */
  actionCtaId?: string;
  /** Array of navigation links in this panel */
  links: NavLink[];
}

export interface PrimaryNavGroup {
  /** L1 navigation item title */
  title: string;
  /** Unique identifier for this nav group */
  id: string;
  /** Whether this is a direct link (no panel) */
  isDirectLink?: boolean;
  /** URL for direct link navigation */
  directLinkUrl?: string;
  /** CTA ID for direct link analytics */
  directLinkCtaId?: string;
  /** Whether this group has a featured panel */
  hasFeaturedPanel?: boolean;
  /** Featured panel image URL */
  featuredImageUrl?: string;
  /** Featured panel image alt text */
  featuredImageAlt?: string;
  /** Featured panel link URL */
  featuredUrl?: string;
  /** Featured panel CTA ID */
  featuredId?: string;
  /** Featured panel title */
  featuredTitle?: string;
  /** Featured panel description */
  featuredDescription?: string;
  /** Featured panel badge text */
  featuredBadgeText?: string;
  /** Featured panel badge icon URL */
  featuredBadgeIconUrl?: string;
  /** Featured panel badge icon alt text */
  featuredBadgeIconAlt?: string;
  /** Video URL — when set, the featured image becomes a video lightbox trigger */
  featuredVideoUrl?: string;
  /** Featured panel heading text */
  featuredPanelTitle?: string;
  /** Featured column size: 'sm' for 3+ columns, 'lg' for 2 columns */
  featuredSize?: 'sm' | 'lg';
  /** Array of secondary navigation panels */
  secondaryNavGroups: SecondaryNavGroup[];
}

export interface NavFooterItem {
  /** Footer link CTA ID */
  id: string;
  /** Footer link text */
  title: string;
  /** Footer link URL */
  url: string;
  /** Footer link target */
  target?: '_blank' | '_self';
  /** Optional icon URL (SWB-style) */
  iconUrl?: string;
  /** Optional plain text displayed before the link (SWB-style) */
  text?: string;
}

export interface SiteWideBannerProps {
  icon?: string;
  text?: string;
  linkText?: string;
  linkUrl?: string;
  ctaId?: string;
  ldAppendix?: string;
  backgroundColor?: string;
}

export interface SearchProps {
  /** Search input placeholder text */
  placeholder?: string;
  /** Search results page path */
  resultsPath?: string;
  /** Search autocomplete module name */
  autocompleteModule?: string;
}

export interface LanguagePickerProps {
  /** Current selected language code */
  currentLanguage: string;
  /** Available languages for selection */
  languages: Array<{ code: string; name: string }>;
  /** Language picker popup heading */
  heading?: string;
  /** Language picker country format */
  countryFormat?: string;
  /** Add locale to path when switching languages */
  addPathLocale?: boolean;
}

export type MegaMenuCtaAction = Extract<CtaAction, 'link' | 'popup'>;

const resolveMegaMenuCtaAction = (
  action: MegaMenuCtaAction | undefined,
  legacyBehavior: '_self' | '_blank' | 'popup' | undefined,
): MegaMenuCtaAction => action ?? (legacyBehavior === 'popup' ? 'popup' : 'link');

const resolveMegaMenuCtaBehavior = (
  legacyBehavior: '_self' | '_blank' | 'popup' | undefined,
): CtaBehavior => legacyBehavior === '_blank' ? 'newTab' : 'sameTab';

export interface CTAButtonProps {
  /** Button display text */
  text: string;
  /** Button destination URL */
  url: string;
  /** Optional analytics tracking ID */
  id?: string;
  /** Analytics LD appendix for tracking */
  ldAppendix?: string;
  /** Link behavior: same tab or new tab. The legacy `popup` value is still accepted. */
  behavior?: '_self' | '_blank' | 'popup';
  /** CTA action. */
  action?: MegaMenuCtaAction;
  /** Popup ID when action is Popup, or when the legacy behavior is `popup`. */
  popupId?: string;
  /** Whether button uses primary styling */
  isPrimary?: boolean;
}

/**
 * @polySemanticUsage About this block: the persistent global navigation bar at the top of every
 * page. Contains the primary site logo, top-level menu links, utility links (e.g. sign in,
 * language selector), and the primary CTA. Appears once per page; never duplicate it or nest it
 * within content areas.
 */
export interface MegaMenuProps {
  // Site Wide Banner fields
  /** Site wide banner icon image URL */
  siteWideBannerIconUrl?: string;
  /** Site wide banner text content */
  siteWideBannerText?: string;
  /** Site wide banner link text */
  siteWideBannerLinkText?: string;
  /** Site wide banner link URL */
  siteWideBannerLinkUrl?: string;
  /** Site wide banner CTA ID for analytics */
  siteWideBannerCtaId?: string;
  /** Site wide banner LD appendix for analytics */
  siteWideBannerLdAppendix?: string;

  // Logo fields
  /** Logo image URL */
  logoImageUrl?: string;
  /** Logo link URL (typically homepage) */
  logoUrl?: string;

  // Primary Navigation (L1) - will be flattened when implemented
  primaryNavGroups?: PrimaryNavGroup[];

  // Nav Footer Links - will be flattened when implemented
  navFooterItems?: NavFooterItem[];

  // Search fields
  /** Enable search functionality */
  enableSearch?: boolean;
  /** Search input placeholder text */
  searchPlaceholder?: string;
  /** Search results page path */
  searchResultsPath?: string;
  /** Search autocomplete module name */
  searchAutocompleteModule?: string;
  /** Search quick links as JSON string: Array<{ text: string; href: string }> */
  searchQuickLinks?: string;
  /**
   * Label shown before the search quick links, also used as their accessible
   * name. Shown exactly as written, with no punctuation added, so each locale
   * writes its own (for example `Liens rapides :`). Translatable so it matches
   * the page language.
   * @polyLabel Search Quick Links Label
   * @polyDefault Quick links:
   */
  searchQuickLinksLabel?: string;
  /**
   * Accessible name for the mobile navigation drawer. Translatable so screen
   * readers announce it in the page language.
   * @polyLabel Mobile Menu Screen Reader Label
   * @polyHelp Name screen readers announce for the mobile navigation drawer. Translated with the page.
   * @polyDefault Navigation menu
   */
  mobileMenuAriaLabel?: string;

  // Language Picker fields
  /** Enable language picker */
  enableLanguagePicker?: boolean;
  /** Current selected language code */
  languagePickerCurrentLanguage?: string;
  /** Available languages as JSON string: Array<{ code: string; name: string; bcp47?: string }> */
  languagePickerLanguages?: string;
  /** Language picker popup heading */
  languagePickerHeading?: string;
  /** Language picker back button label (localized, e.g. "Back" / "Atrás") */
  languagePickerBackText?: string;
  /** Language picker country format */
  languagePickerCountryFormat?: string;
  /** Add locale to path when switching languages */
  languagePickerAddPathLocale?: boolean;

  // Primary CTA fields
  /** Primary CTA button text */
  primaryCtaText?: string;
  /** Primary CTA button URL */
  primaryCtaUrl?: string;
  /** Primary CTA button ID */
  primaryCtaId?: string;
  /** Primary CTA LD appendix */
  primaryCtaLdAppendix?: string;
  /**
   * Primary CTA behavior. The legacy `popup` value remains supported for existing content.
   * @polyHelp Use Same tab or New tab for links. Use the Action field for new Popup CTAs; when Action is set, it takes priority. The Popup option here remains for existing content.
   */
  primaryCtaBehavior?: '_self' | '_blank' | 'popup';
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Popup ID and does not use the CTA URL or tab Behavior fields.
   * @polyDefault link
   * @polyTranslatable false
   */
  primaryCtaAction?: MegaMenuCtaAction;
  /**
   * Popup ID to open when Primary CTA Action is Popup. Also supports existing CTAs whose legacy Behavior is Popup.
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  primaryCtaPopupId?: string;

  // Secondary CTA fields
  /** Secondary CTA button text */
  secondaryCtaText?: string;
  /** Secondary CTA button URL */
  secondaryCtaUrl?: string;
  /** Secondary CTA button ID */
  secondaryCtaId?: string;
  /** Secondary CTA LD appendix */
  secondaryCtaLdAppendix?: string;
  /**
   * Secondary CTA behavior. The legacy `popup` value remains supported for existing content.
   * @polyHelp Use Same tab or New tab for links. Use the Action field for new Popup CTAs; when Action is set, it takes priority. The Popup option here remains for existing content.
   */
  secondaryCtaBehavior?: '_self' | '_blank' | 'popup';
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Popup ID and does not use the CTA URL or tab Behavior fields.
   * @polyDefault link
   * @polyTranslatable false
   */
  secondaryCtaAction?: MegaMenuCtaAction;
  /**
   * Popup ID to open when Secondary CTA Action is Popup. Also supports existing CTAs whose legacy Behavior is Popup.
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  secondaryCtaPopupId?: string;
  /**
   * Secondary CTA icon name (e.g., 'lock' for sign in)
   * @polyType SelectList
   * @polyOptions Cart=cart,Storefront=storefront,Package=package,Analytics=analytics,Star=star,Check=check,Alert Circle=alert-circle,Bolt=bolt,Arrow Forward=arrow-forward,Lock=lock,Menu=menu,Play=play,Gen AI=gen-ai,Pause=pause,Search=search,Close=close,Globe=globe,Language=language,Arrow Left=arrow-left,Chevron Down=chevron-down,Chevron Right=chevron-right,Chevron Left=chevron-left,Chevron Right Large=chevron-right-lg,Chevron Left Large=chevron-left-lg
   * @polyTranslatable false
   */
  secondaryCtaIconName?: CustomIconName;
}

export const MegaMenu: React.FC<MegaMenuProps> = ({
  // Site Wide Banner
  siteWideBannerIconUrl,
  siteWideBannerText,
  siteWideBannerLinkText,
  siteWideBannerLinkUrl,
  siteWideBannerCtaId,
  siteWideBannerLdAppendix,
  // Logo
  logoImageUrl,
  logoUrl,
  // Navigation
  primaryNavGroups = [],
  navFooterItems,
  // Search fields
  enableSearch = false,
  searchPlaceholder,
  searchResultsPath,
  searchAutocompleteModule: _searchAutocompleteModule, // deferred: dynamic autocomplete loading
  searchQuickLinks,
  searchQuickLinksLabel,
  mobileMenuAriaLabel,
  // Language Picker fields
  enableLanguagePicker = false,
  languagePickerCurrentLanguage,
  languagePickerLanguages,
  languagePickerHeading,
  languagePickerBackText,
  languagePickerCountryFormat: _languagePickerCountryFormat, // deferred: country name formatting
  languagePickerAddPathLocale,
  // CTAs
  primaryCtaText,
  primaryCtaUrl,
  primaryCtaId,
  primaryCtaLdAppendix,
  primaryCtaBehavior,
  primaryCtaAction,
  primaryCtaPopupId,
  secondaryCtaText,
  secondaryCtaUrl,
  secondaryCtaId,
  secondaryCtaLdAppendix,
  secondaryCtaBehavior,
  secondaryCtaAction,
  secondaryCtaPopupId,
  secondaryCtaIconName,
}) => {
  // ── State ──────────────────────────────────────────────────────────────
  const [selectedPrimaryNavGroupId, setSelectedPrimaryNavGroupId] = useState<string>('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const uid = useId();
  const drawerId = `poly-mega-menu-drawer-${uid}`;
  const langDrawerId = `poly-mega-menu-lang-drawer-${uid}`;
  const mobileId = `poly-mega-menu-mobile-${uid}`;

  const [isNavMenuExpanded, setIsNavMenuExpanded] = useState(false);
  const [isLanguagePickerExpanded, setIsLanguagePickerExpanded] = useState(false);
  const [drawerAnimated, setDrawerAnimated] = useState(false);
  const [isSticky, setIsSticky] = useState(false);

  // ── Refs ───────────────────────────────────────────────────────────────
  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null);
  const desktopPanelFocusRef = useRef<HTMLElement | null>(null);
  const selectedNavRef = useRef(selectedPrimaryNavGroupId);
  selectedNavRef.current = selectedPrimaryNavGroupId;
  const stickyRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLElement>(null);

  const DESKTOP_BREAKPOINT = 1024;

  // ── Callbacks ──────────────────────────────────────────────────────────

  const toggleSelectedPrimaryNavGroupId = (id: string) => {
    setIsSearchOpen(false);
    setSelectedPrimaryNavGroupId(prevId => prevId === id ? '' : id);
  };

  const handleSearchToggle = useCallback(() => {
    setIsSearchOpen(prev => {
      if (!prev) {
        setSelectedPrimaryNavGroupId('');
        setIsLanguagePickerExpanded(false);
      }
      return !prev;
    });
  }, []);

  const toggleNavMenu = useCallback(() => {
    setIsSearchOpen(false);
    setIsLanguagePickerExpanded(false);
    setDrawerAnimated(true);
    setIsNavMenuExpanded(prev => {
      if (!prev) {
        previouslyFocusedElementRef.current = document.activeElement as HTMLElement;
      } else {
        setSelectedPrimaryNavGroupId('');
      }
      return !prev;
    });
  }, []);

  const toggleLanguagePickerDrawer = useCallback(() => {
    setIsLanguagePickerExpanded(prev => !prev);
  }, []);

  const closeLanguagePickerDrawer = useCallback(() => {
    setIsLanguagePickerExpanded(false);
  }, []);

  const closeAllPanels = () => {
    setSelectedPrimaryNavGroupId('');
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      closeAllPanels();
    }
  };

  // ── Effects ────────────────────────────────────────────────────────────

  // Breakpoint transition via matchMedia — only fires on actual breakpoint crossings
  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`);
    const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
      setIsSearchOpen(false);
      setDrawerAnimated(false);
      if (e.matches) {
        // Crossed to desktop — close the mobile drawer but keep any open panel open
        setIsNavMenuExpanded(false);
      } else if (selectedNavRef.current) {
        // Crossed to mobile with panel open — auto-open drawer
        setIsNavMenuExpanded(true);
      }
    };
    mq.addEventListener('change', handleChange);
    return () => mq.removeEventListener('change', handleChange);
  }, []);

  // Escape key handler for mobile drawer
  useEffect(() => {
    if (!isNavMenuExpanded) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // A dialog opened from the drawer (a popup CTA) sits on top and closes on its own.
        if (isSearchOpen || hasOpenVisibleDialog()) return;
        if (isLanguagePickerExpanded) {
          setIsLanguagePickerExpanded(false);
          return;
        }
        setIsNavMenuExpanded(false);
        setSelectedPrimaryNavGroupId('');
        previouslyFocusedElementRef.current?.focus();
        previouslyFocusedElementRef.current = null;
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isNavMenuExpanded, isSearchOpen, isLanguagePickerExpanded]);

  // Restore focus when mobile drawer closes
  useEffect(() => {
    if (!isNavMenuExpanded && previouslyFocusedElementRef.current) {
      previouslyFocusedElementRef.current.focus();
      previouslyFocusedElementRef.current = null;
    }
  }, [isNavMenuExpanded]);

  // Body scroll lock handled via CSS :has() in MegaMenu.scss — no JS needed

  // Focus trap for mobile drawer
  useEffect(() => {
    if (!isNavMenuExpanded) return;
    const handleTabTrap = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || hasOpenVisibleDialog()) return;
      let focusable: HTMLElement[];
      if (isLanguagePickerExpanded) {
        const langDrawer = document.getElementById(langDrawerId);
        if (!langDrawer) return;
        focusable = Array.from(langDrawer.querySelectorAll<HTMLElement>(
          'button, a[href], input, [tabindex]:not([tabindex="-1"])'
        )).filter(el => el.offsetParent !== null);
      } else {
        const mobileView = document.getElementById(mobileId);
        const drawer = document.getElementById(drawerId);
        if (!mobileView || !drawer) return;
        focusable = [
          ...Array.from(mobileView.querySelectorAll<HTMLElement>(
            '.poly-mega-menu__nav-container button, .poly-mega-menu__nav-container a[href]'
          )),
          ...Array.from(drawer.querySelectorAll<HTMLElement>(
            'button, a[href], input, [tabindex]:not([tabindex="-1"])'
          )),
        ].filter(el => el.offsetParent !== null);
      }
      wrapTabFocus(e, focusable);
    };
    document.addEventListener('keydown', handleTabTrap);
    return () => document.removeEventListener('keydown', handleTabTrap);
  }, [isNavMenuExpanded, isLanguagePickerExpanded]);

  // Sticky navigation via IntersectionObserver
  useEffect(() => {
    const sentinel = stickyRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => { setIsSticky(!entry.isIntersecting); },
      { threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  // Focus management for desktop panels
  useEffect(() => {
    if (selectedPrimaryNavGroupId) {
      desktopPanelFocusRef.current = document.activeElement as HTMLElement;
    } else if (desktopPanelFocusRef.current) {
      desktopPanelFocusRef.current.focus();
      desktopPanelFocusRef.current = null;
    }
  }, [selectedPrimaryNavGroupId]);

  // ── Derived values ─────────────────────────────────────────────────────

  const effectiveSticky = isSticky || isNavMenuExpanded;
  const hasSiteWideBanner = siteWideBannerText || siteWideBannerLinkText;

  // While the nav is pinned to the top of the window it covers whatever is
  // below it, so publish its height for other sticky elements (rails,
  // SecondaryNav) to sit under. The height differs between the mobile and
  // desktop bars, so it is measured rather than read from a token.
  useEffect(() => {
    const nav = navRef.current;
    if (!effectiveSticky || !nav) return;
    const publish = () => publishStickyOffset(nav.offsetHeight);
    publish();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(publish);
    observer?.observe(nav);
    return () => {
      observer?.disconnect();
      publishStickyOffset(0);
    };
  }, [effectiveSticky]);

  const parsedLanguages = useMemo<Language[]>(() => {
    if (!enableLanguagePicker || !languagePickerLanguages) return [];
    try { return JSON.parse(languagePickerLanguages); } catch {
      console.warn('[MegaMenu] Failed to parse languagePickerLanguages JSON');
      return [];
    }
  }, [enableLanguagePicker, languagePickerLanguages]);

  // Resolve effective current language: URL param takes precedence over CMS prop.
  // After a language switch navigation, the URL contains mons_sel_locale=es_US
  // but the CMS prop may still reflect the default (e.g. en-US).
  //
  // SSR-safe: window.location is undefined during server render. To avoid
  // hydration mismatches (different aria-current, class names, href values
  // between server HTML and client first render), we capture pathname/search
  // into state in a useEffect. Initial render uses the CMS prop only; after
  // mount we re-render with URL-derived values.
  const [clientUrl, setClientUrl] = useState<{ pathname: string; search: string }>({
    pathname: '',
    search: '',
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    setClientUrl({
      pathname: window.location.pathname,
      search: window.location.search,
    });
  }, []);

  const effectiveCurrentLanguage = useMemo(() => {
    if (!enableLanguagePicker || parsedLanguages.length === 0) return languagePickerCurrentLanguage;
    const urlLocale = detectLocaleFromUrl(
      parsedLanguages.map(l => l.code),
      clientUrl.search
    );
    return urlLocale ?? languagePickerCurrentLanguage;
  }, [enableLanguagePicker, parsedLanguages, languagePickerCurrentLanguage, clientUrl.search]);

  // ── Shared slots (built once, passed to Desktop + Mobile) ─────────────

  const resolvedPrimaryCtaAction = resolveMegaMenuCtaAction(primaryCtaAction, primaryCtaBehavior);
  const resolvedSecondaryCtaAction = resolveMegaMenuCtaAction(secondaryCtaAction, secondaryCtaBehavior);
  const resolvedPrimaryCtaBehavior = resolveMegaMenuCtaBehavior(primaryCtaBehavior);
  const resolvedSecondaryCtaBehavior = resolveMegaMenuCtaBehavior(secondaryCtaBehavior);
  const showPrimaryCta = Boolean(
    primaryCtaText && hasCtaDestination(primaryCtaUrl, resolvedPrimaryCtaAction, primaryCtaPopupId),
  );
  const showSecondaryCta = Boolean(
    secondaryCtaText && hasCtaDestination(secondaryCtaUrl, resolvedSecondaryCtaAction, secondaryCtaPopupId),
  );

  const logoSlot = useMemo(() => logoImageUrl ? (
    <div className="poly-mega-menu__logo">
      <Logo imageUrl={logoImageUrl} url={logoUrl} altText="Amazon" />
    </div>
  ) : undefined, [logoImageUrl, logoUrl]);

  const ctaSlot = useMemo(() => showPrimaryCta || showSecondaryCta ? (
    <div className="poly-mega-menu__actions">
      <div className="poly-mega-menu__ctas">
        <CtaTrackingPrefixContext.Provider value={CTA_PREFIXES.nav}>
          {showSecondaryCta && (
            <CtaButton
              text={secondaryCtaText}
              href={secondaryCtaUrl}
              action={resolvedSecondaryCtaAction}
              popupTargetId={secondaryCtaPopupId}
              behavior={resolvedSecondaryCtaBehavior}
              data-cta-id={secondaryCtaId}
              ldAppendix={secondaryCtaLdAppendix}
              colorVariant="tertiary"
              size="nav"
            />
          )}
          {showPrimaryCta && (
            <CtaButton
              text={primaryCtaText}
              href={primaryCtaUrl}
              action={resolvedPrimaryCtaAction}
              popupTargetId={primaryCtaPopupId}
              behavior={resolvedPrimaryCtaBehavior}
              data-cta-id={primaryCtaId}
              ldAppendix={primaryCtaLdAppendix}
              size="nav"
            />
          )}
        </CtaTrackingPrefixContext.Provider>
      </div>
    </div>
  ) : undefined, [
    primaryCtaText, primaryCtaUrl, resolvedPrimaryCtaAction, primaryCtaPopupId, resolvedPrimaryCtaBehavior,
    primaryCtaId, primaryCtaLdAppendix, showPrimaryCta,
    secondaryCtaText, secondaryCtaUrl, resolvedSecondaryCtaAction, secondaryCtaPopupId,
    resolvedSecondaryCtaBehavior, secondaryCtaId, secondaryCtaLdAppendix, showSecondaryCta,
  ]);

  const bannerRightContent = useMemo(() => {
    if (!enableSearch && !enableLanguagePicker) return undefined;
    return (
      <>
        {enableSearch && (
          <button
            type="button"
            className="poly-mega-menu__search-trigger"
            aria-label={searchPlaceholder || 'Search'}
            onClick={handleSearchToggle}
          >
            <CustomIcon name="search" className="poly-mega-menu__search-trigger-icon" aria-hidden="true" />
            <span>{searchPlaceholder || 'Search'}</span>
          </button>
        )}
        {enableLanguagePicker && parsedLanguages.length > 0 && effectiveCurrentLanguage && (
          <LanguagePicker
            currentLanguage={effectiveCurrentLanguage}
            languages={parsedLanguages}
            heading={languagePickerHeading}
            addPathLocale={languagePickerAddPathLocale}
            isOpen={isLanguagePickerExpanded}
            onToggle={toggleLanguagePickerDrawer}
            onClose={closeLanguagePickerDrawer}
            currentPathname={clientUrl.pathname}
            currentSearch={clientUrl.search}
          />
        )}
      </>
    );
  }, [enableSearch, enableLanguagePicker, searchPlaceholder, handleSearchToggle, parsedLanguages, effectiveCurrentLanguage, languagePickerHeading, languagePickerAddPathLocale, isLanguagePickerExpanded, toggleLanguagePickerDrawer, closeLanguagePickerDrawer, clientUrl.pathname, clientUrl.search]);

  const parsedQuickLinks = useMemo<Array<{ text: string; href: string }>>(() => {
    if (!searchQuickLinks) return [];
    try { return JSON.parse(searchQuickLinks); } catch {
      console.warn('[MegaMenu] Failed to parse searchQuickLinks JSON');
      return [];
    }
  }, [searchQuickLinks]);

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <>
      {/* Site Wide Banner */}
      {hasSiteWideBanner && (
        <div className={`poly-mega-menu__swb-wrapper${effectiveSticky ? ' poly-mega-menu__swb-wrapper--hidden' : ''}`}>
          <SiteWideBanner
            iconUrl={siteWideBannerIconUrl}
            text={siteWideBannerText}
            linkText={siteWideBannerLinkText}
            linkUrl={siteWideBannerLinkUrl}
            ctaId={siteWideBannerCtaId}
            ldAppendix={siteWideBannerLdAppendix}
            rightContent={bannerRightContent}
          />
        </div>
      )}

      {/* Search Overlay */}
      {isSearchOpen && (
        <div
          className="poly-mega-menu__search-overlay"
          onKeyDown={(e) => { if (e.key === 'Escape') setIsSearchOpen(false); }}
        >
          <div className="poly-mega-menu__search-overlay-row">
            <SearchBar placeholder={searchPlaceholder} resultsPath={searchResultsPath} focusOnMount />
            <button
              type="button"
              className="poly-mega-menu__search-overlay-close"
              onClick={() => setIsSearchOpen(false)}
              aria-label="Close search"
            >
              <CustomIcon name="close" size="1.5em" aria-hidden="true" />
            </button>
          </div>
          {parsedQuickLinks.length > 0 && (
            <nav className="poly-mega-menu__search-quick-links" aria-label={searchQuickLinksLabel || 'Quick links'}>
              <span className="poly-mega-menu__search-quick-links-label">{searchQuickLinksLabel || 'Quick links:'}</span>
              {parsedQuickLinks.map((link, i) => (
                <a key={i} href={link.href} className="poly-mega-menu__search-quick-link">{link.text}</a>
              ))}
            </nav>
          )}
        </div>
      )}

      {/* Sentinel for IntersectionObserver sticky nav */}
      <div ref={stickyRef} className="poly-mega-menu__sticky-sentinel" />

      <nav ref={navRef} className={`poly-mega-menu${isNavMenuExpanded ? ' expanded' : ''}${effectiveSticky ? ' poly-mega-menu--sticky' : ''}`} role="navigation">
        <DesktopNav
          logoSlot={logoSlot}
          ctaSlot={ctaSlot}
          primaryNavGroups={primaryNavGroups}
          navFooterItems={navFooterItems}
          selectedPrimaryNavGroupId={selectedPrimaryNavGroupId}
          toggleSelectedPrimaryNavGroupId={toggleSelectedPrimaryNavGroupId}
          closeAllPanels={closeAllPanels}
          handleKeyDown={handleKeyDown}
        />

        <MobileDrawer
          mobileId={mobileId}
          drawerId={drawerId}
          langDrawerId={langDrawerId}
          logoSlot={logoSlot}
          primaryNavGroups={primaryNavGroups}
          selectedPrimaryNavGroupId={selectedPrimaryNavGroupId}
          toggleSelectedPrimaryNavGroupId={toggleSelectedPrimaryNavGroupId}
          navFooterItems={navFooterItems}
          isNavMenuExpanded={isNavMenuExpanded}
          drawerAnimated={drawerAnimated}
          toggleNavMenu={toggleNavMenu}
          mobileMenuAriaLabel={mobileMenuAriaLabel}
          enableSearch={enableSearch}
          searchPlaceholder={searchPlaceholder}
          handleSearchToggle={handleSearchToggle}
          enableLanguagePicker={enableLanguagePicker}
          parsedLanguages={parsedLanguages}
          languagePickerCurrentLanguage={effectiveCurrentLanguage}
          isLanguagePickerExpanded={isLanguagePickerExpanded}
          toggleLanguagePickerDrawer={toggleLanguagePickerDrawer}
          primaryCtaText={primaryCtaText}
          primaryCtaUrl={primaryCtaUrl}
          primaryCtaId={primaryCtaId}
          primaryCtaLdAppendix={primaryCtaLdAppendix}
          primaryCtaBehavior={resolvedPrimaryCtaBehavior}
          primaryCtaAction={resolvedPrimaryCtaAction}
          primaryCtaTargetPopupId={primaryCtaPopupId}
          secondaryCtaText={secondaryCtaText}
          secondaryCtaUrl={secondaryCtaUrl}
          secondaryCtaId={secondaryCtaId}
          secondaryCtaLdAppendix={secondaryCtaLdAppendix}
          secondaryCtaBehavior={resolvedSecondaryCtaBehavior}
          secondaryCtaAction={resolvedSecondaryCtaAction}
          secondaryCtaTargetPopupId={secondaryCtaPopupId}
          secondaryCtaIconName={secondaryCtaIconName}
        />
      </nav>

      {/* Nested language picker drawer — full-screen overlay */}
      {enableLanguagePicker && parsedLanguages.length > 0 && (
        <div
          className={`poly-mega-menu__lang-drawer${isLanguagePickerExpanded && isNavMenuExpanded ? ' poly-mega-menu__lang-drawer--open' : ''}`}
          id={langDrawerId}
          role="dialog"
          aria-label={languagePickerHeading || 'Select your preferred language'}
        >
          <header className="poly-mega-menu__lang-drawer-header">
            <button
              type="button"
              className="poly-mega-menu__lang-drawer-back"
              aria-label={languagePickerBackText || 'Back'}
              onClick={toggleLanguagePickerDrawer}
            >
              <CustomIcon name="arrow-left" size="1.25em" aria-hidden="true" />
              <span>{languagePickerBackText || 'Back'}</span>
            </button>
            <span className="poly-mega-menu__lang-drawer-current">
              <CustomIcon name="language" size="1.25em" aria-hidden="true" />
              <span>{parsedLanguages.find(l => l.code === effectiveCurrentLanguage)?.name || effectiveCurrentLanguage}</span>
            </span>
          </header>
          <h3 className="poly-mega-menu__lang-drawer-heading">
            {languagePickerHeading || 'Select language'}
          </h3>
          <ul className="poly-mega-menu__lang-drawer-list" role="list">
            {parsedLanguages.map((lang) => {
              const tag = lang.bcp47 || lang.code.split('-')[0].toUpperCase();
              return (
                <li key={lang.code} className="poly-mega-menu__lang-drawer-item">
                  <a
                    href={buildLanguageUrl(lang.code, effectiveCurrentLanguage ?? '', languagePickerAddPathLocale ?? false, clientUrl.pathname, clientUrl.search)}
                    className={`poly-mega-menu__lang-drawer-link${lang.code === effectiveCurrentLanguage ? ' poly-mega-menu__lang-drawer-link--current' : ''}`}
                    aria-current={lang.code === effectiveCurrentLanguage ? 'true' : undefined}
                  >
                    <span>{lang.name} – {tag}</span>
                    {lang.code === effectiveCurrentLanguage && (
                      <CustomIcon name="check" size="1em" className="poly-mega-menu__lang-drawer-check" aria-hidden="true" />
                    )}
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </>
  );
};
